import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, KeyRound, Sparkles } from "lucide-react";
import { IMAGES } from "@/data/services";
import { scrollToId } from "@/hooks/useLenis";

export const Hero = ({ onOpenDrawer }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });

  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  // Stage 1 — the centered key emblem opening moment
  const openingScale = useTransform(scrollYProgress, [0, 0.4], [1, 0.65]);
  const openingFade = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const openingY = useTransform(scrollYProgress, [0, 0.4], ["0%", "-14%"]);

  // Stage 2 — the headline reveal
  const revealFade = useTransform(scrollYProgress, [0.3, 0.6], [0, 1]);
  const revealY = useTransform(scrollYProgress, [0.3, 0.6], [60, 0]);
  const revealPointer = useTransform(revealFade, (v) => (v > 0.4 ? "auto" : "none"));

  // Sticky top bar — invisible during the opening moment, fades in on scroll
  const headerFade = useTransform(scrollYProgress, [0.25, 0.45], [0, 1]);
  const headerPointer = useTransform(headerFade, (v) => (v > 0.4 ? "auto" : "none"));

  // Scroll hint — only while the opening holds
  const hintFade = useTransform(scrollYProgress, [0, 0.12], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-[190vh]">
      <div className="sticky top-0 flex h-screen flex-col overflow-hidden">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <img src={IMAGES.hero} alt="Northern lights over Iceland" className="h-[125%] w-full object-cover opacity-75" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#070709] via-[#070709]/20 to-[#070709]/55" />
          <div className="absolute -left-40 top-1/4 h-[480px] w-[480px] rounded-full bg-emerald-500/10 blur-[140px]" />
          <div className="absolute -right-40 top-10 h-[420px] w-[420px] rounded-full bg-amber-500/10 blur-[140px]" />
        </motion.div>

        {/* Sticky top bar — appears only after the opening moment */}
        <motion.header
          style={{ opacity: headerFade, pointerEvents: headerPointer }}
          className="absolute top-0 z-20 flex w-full items-center justify-between border-b border-amber-500/20 bg-[#070709]/80 px-5 py-4 backdrop-blur-xl sm:px-10"
        >
          <div className="flex items-center gap-3">
            <img src="/assets/logo-crest.png" alt="" className="h-9 w-auto mix-blend-screen" />
            <span className="font-luxe text-xs uppercase tracking-[0.3em] text-amber-100 sm:text-sm">Exclusive Reykjavik</span>
          </div>
          <button
            data-testid="hero-nav-request-button"
            onClick={onOpenDrawer}
            className="flex items-center gap-2 border border-amber-500/40 px-4 py-2 text-[10px] uppercase tracking-[0.25em] text-amber-200/90 transition-colors duration-300 hover:bg-amber-500 hover:text-black sm:px-5 sm:py-2.5 sm:text-xs"
          >
            <KeyRound className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Your Request</span>
          </button>
        </motion.header>

        {/* Stage 1 — centered logo-led opening */}
        <motion.div
          style={{ opacity: openingFade, scale: openingScale, y: openingY }}
          className="absolute inset-0 z-10 flex flex-col items-center justify-center px-6 pb-24 text-center"
        >
          <motion.img
            src="/assets/logo-crest.png"
            alt="Exclusive Reykjavik key crest"
            data-testid="hero-logo"
            className="h-36 w-auto drop-shadow-[0_0_60px_rgba(212,175,55,0.25)] sm:h-48 md:h-56"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
          />
          <motion.p
            className="mt-10 font-luxe text-2xl font-semibold uppercase tracking-[0.35em] text-amber-100 sm:text-4xl"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            Exclusive Reykjavik
          </motion.p>
          <motion.p
            className="mt-5 text-xs font-medium uppercase tracking-[0.4em] text-amber-400/90 sm:text-sm"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
          >
            Key to the City · Est. 2015
          </motion.p>
        </motion.div>

        {/* Stage 2 — headline reveal */}
        <motion.div
          style={{ opacity: revealFade, y: revealY, pointerEvents: revealPointer }}
          className="absolute inset-0 z-10 flex flex-col items-center justify-center px-6 text-center"
        >
          <h1
            data-testid="hero-title"
            className="font-luxe text-4xl font-semibold uppercase leading-[1.15] tracking-[0.08em] text-neutral-50 sm:text-6xl lg:text-7xl"
          >
            Iceland,
            <span
              className="gold-shimmer mt-2 block font-serif normal-case italic font-bold"
              style={{ fontFamily: "'Bodoni Moda', 'Cormorant Garamond', serif" }}
            >
              unlocked.
            </span>
          </h1>
          <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-neutral-300 sm:text-lg">
            Hotels, helicopters, private chefs, bottle service and parties staged inside lava
            caves, ice caves and hidden hot springs — composed for you, anywhere on Earth.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <button
              data-testid="hero-cta-explore"
              onClick={() => scrollToId("#services")}
              className="group flex items-center gap-3 bg-amber-500 px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-colors duration-300 hover:bg-amber-300"
            >
              Compose Your Journey
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
            </button>
            <button
              data-testid="hero-cta-itinerary"
              onClick={() => scrollToId("#itinerary")}
              className="flex items-center gap-3 border border-neutral-600 px-7 py-3.5 text-xs uppercase tracking-[0.25em] text-neutral-200 transition-colors duration-300 hover:border-amber-400 hover:text-amber-200"
            >
              <Sparkles className="h-4 w-4" />
              AI Itinerary
            </button>
          </div>
        </motion.div>

        {/* Scroll hint — only while the opening holds */}
        <motion.div
          style={{ opacity: hintFade }}
          data-testid="hero-scroll-hint"
          className="absolute bottom-10 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-3 [@media(max-height:460px)]:hidden"
        >
          <span className="text-[9px] font-medium uppercase tracking-[0.45em] text-amber-300/70">Scroll to unlock</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            className="h-12 w-px bg-gradient-to-b from-transparent via-amber-400/70 to-transparent"
          />
        </motion.div>
      </div>
    </section>
  );
};
